import { Experiment } from '@amplitude/experiment-js-client';
import { noop } from 'ui-components/default-props/noop';

type ExperimentClient = ReturnType<typeof Experiment.initialize>;

// Amplitude's fetch signature only accepts primitive (or array-of-primitive) user properties.
type ExperimentUserProperties = { [propertyName: string]: string | number | boolean | Array<string | number | boolean> };

let client: ExperimentClient | null = null;
let isExperimentReady = false;
let readyListeners: Array<() => void> = [];

/**
 * Boots the Amplitude Experiment client once, after the user is known. Exposures are forwarded into
 * the existing Segment tracker as `$exposure` events so no separate analytics pipeline is needed.
 */
export const initializeAmplitudeExperiment = async (userId: string, userProperties: Record<string, unknown>) => {
  const deploymentKey = window.__amplitudeExperimentDeploymentKey;
  if (!deploymentKey || client) {
    return;
  }

  client = Experiment.initialize(deploymentKey, {
    automaticExposureTracking: true,
    exposureTrackingProvider: {
      track: (exposure) => window.analytics?.track('$exposure', exposure as unknown as Record<string, unknown>),
    },
  });

  await client.fetch({
    user_id: userId,
    user_properties: userProperties as ExperimentUserProperties,
  });

  isExperimentReady = true;
  readyListeners.forEach((l) => l());
  readyListeners = [];
};

/**
 * Subscribe to be notified when the experiment client has fetched variants. If already ready,
 * the callback is invoked synchronously. Returns an unsubscribe function.
 */
export const subscribeToReady = (callback: () => void): (() => void) => {
  if (isExperimentReady) {
    callback();
    return noop;
  }
  readyListeners.push(callback);
  return () => {
    readyListeners = readyListeners.filter((l) => l !== callback);
  };
};

export const getVariant = (flagKey: string) => client?.variant(flagKey);
